import { RotationBoard } from './Rotation';
import { TextSchedules, useTextLog } from './Texts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { actualRow, breakLevel, cutFrom, DAY_BLOCKS, emptyRow, generateBreaks, OVERNIGHT_ENDS, paintRange, recalcAll, summarize } from '@mw/engine';
import { ApiError, get, post, put, type Me } from './api';
import { CoverLine, CoverPick, useCoverDay } from './Cover';
import { EmailPdf } from './EmailPdf';
import { BusyControls, BusyStrip, busyView, useBusy } from './BusyHours';
import { Confirm, Modal, useToast, type ConfirmSpec } from './ui';

/**
 * Day board (prototype "Day board" tab) and the week bar (chip, Post week / Post changes, week picker,
 * "+ Start the week ending …"). Press and drag along a row to paint a shift; start on a filled block to
 * erase. Breaks and lunches are placed by the prototype's engine as you paint (only the changed person's
 * breaks move); "Recalculate all breaks" offers a fresh layout for everyone.
 */

const OFF = emptyRow();   // 112 blocks: 5 AM to 9 AM next morning (overnight party, owner 10/23)
const FIRST_HOUR = 5, LAST_HOUR = 29;
const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface TimeOffDay { allDay: boolean; fromBlock: number | null; toBlock: number | null; note: string }
interface Person { id: number; name: string; short: string; level: number; canManagerSpot: boolean; canLeadSpot: boolean; maxHours: number | null; maxDays: number | null; away: boolean; home: string; office: boolean; position: string | null; avail: string[]; timeOff: Record<string, TimeOffDay> }
interface Change { id: number; date: string; employeeId: number; kind: 'left_early' | 'called_out' | 'called_in' | 'arrived_late'; atBlock: number | null; fromBlock: number | null; toBlock: number | null; reason: string | null; sickHours: number | null; by: string | null }
const REASONS: Record<string, string> = { sick: 'Sick', slow: 'Slow', personal: 'Personal', injury: 'Injury' };
// "Arrived late" (owner 10/22): no Sick; Slow = the store asked them to start later
const LATE_REASONS: Record<string, string> = { slow: 'Slow (asked to start later)', personal: 'Personal', injury: 'Injury' };
/** Where a left-early / called-out row stops counting (called out: from the start of the shift). */
function leftOf(day: Day, id: number): { b: number; c: Change } | null {
  const c = day.changes.find((x) => x.employeeId === id && (x.kind === 'left_early' || x.kind === 'called_out')); if (!c) return null;
  const row = day.rows[id] ?? OFF;
  return { b: c.kind === 'left_early' ? c.atBlock ?? 0 : Math.max(0, row.search(/[^.]/)), c };
}
/** Arrived late (owner 10/22): the time they arrived; before it counts as off. */
function lateOf(day: Day, id: number): { b: number; c: Change } | null {
  const c = day.changes.find((x) => x.employeeId === id && x.kind === 'arrived_late');
  return c ? { b: c.atBlock ?? 0, c } : null;
}
/** The row as it actually went: off before a late arrival and from leaving early / calling out. */
function actualOf(day: Day, id: number, row: string): string {
  return actualRow(row, leftOf(day, id)?.b ?? null, lateOf(day, id)?.b ?? null);
}
type Needs = { staff: Record<string, number | null>; lead: Record<string, number | null>; mgr: Record<string, number | null> };
interface Day {
  date: string; label: string; traffic: string; breakSeed: number; rows: Record<string, string>;
  /** "Overnight party" (owner 10/23): the day's end (96 = 5 AM, up to 112 = 9 AM) and where it starts (after the night before's party) */
  overnightEnd: number | null; dayEnd: number; dayStart: number;
  switchHours: Record<string, number>; needCustom: Needs | null; customOn: boolean; elsewhere: Record<string, string>; changes: Change[];
  /** our people working at another store that day (hours count here) */
  sharedOut: Record<string, { store: string; blocks: string }>;
  /** shifts here that count at another store, or Operations fill-ins */
  sharedIn: Record<string, { from: string; office: boolean }>;
}
const TRAFFIC: [string, string][] = [['slow', 'Slow all day'], ['busy', 'Busy all day'], ['slow_am_busy_pm', 'Slow AM, busy PM'], ['busy_am_slow_pm', 'Busy AM, slow PM']];
const trName = (t: string) => TRAFFIC.find((x) => x[0] === t)?.[1] ?? t;
interface WeekResp {
  location: { id: number; name: string }; weekStart: string; weekEnd: string; today: string;
  week: { id: number; status: 'draft' | 'posted'; startedHow: string } | null;
  chip: { chip: string; cls: string; tip: string };
  unposted: { employeeId: number; days: number[]; name: string }[];
  canEdit: boolean; canEditPresets: boolean; canMidDay: boolean; canFix: boolean; presets: Record<string, Needs>; days: Day[]; people: Person[];
  canEditOffice: boolean; otherStores: { id: number; name: string }[];
}
interface WeeksResp { thisWeek: string; nextWeek: string; canEdit: boolean; weeks: { id: number; weekStart: string; status: string; label: string }[] }

function tlabel(b: number) {
  const m = FIRST_HOUR * 60 + 15 * b; const h = Math.floor(m / 60) % 24; const mm = m % 60;
  return `${h % 12 === 0 ? 12 : h % 12}:${mm < 10 ? '0' : ''}${mm}${h >= 12 ? 'p' : 'a'}`;
}
function hourLabel(H: number) { const h = H % 24; return `${h % 12 === 0 ? 12 : h % 12} ${h >= 12 ? 'PM' : 'AM'}`; }
function isoAdd(iso: string, n: number) { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
function md(iso: string) { const [, m, d] = iso.split('-'); return `${Number(m)}/${Number(d)}`; }
const weekEndShort = (start: string) => 'Sat ' + md(isoAdd(start, 6));
const KIND: Record<string, string> = { W: 'S', B: 'R', L: 'M', '.': '' };

/** The prototype's fitSize: shrinks the breaks text (12px down to 7px, half-pixel steps) to fit its column. */
let measure: CanvasRenderingContext2D | null = null;
const fitCache = new Map<string, number>();
function fitSize(txt: string): number {
  if (!txt) return 12;
  const hit = fitCache.get(txt); if (hit) return hit;
  let w12: number | null = null;
  try {
    measure ??= document.createElement('canvas').getContext('2d');
    if (measure) { measure.font = '12px ' + getComputedStyle(document.body).fontFamily; w12 = measure.measureText(txt).width; }
  } catch { w12 = null; }
  if (!w12) w12 = txt.length * 12 * 0.45;
  const z = Math.max(7, Math.min(12, Math.floor(12 * 138 / w12 * 2) / 2));
  fitCache.set(txt, z);
  return z;
}

export function DayBoardPage({ me, locationId, view = 'day' }: { me: Me; locationId: number; view?: 'day' | 'week' | 'rotation' }) {
  const [weeks, setWeeks] = useState<WeeksResp | null>(null);
  const [start, setStart] = useState<string | null>(null);
  const [data, setData] = useState<WeekResp | null>(null);
  const [day, setDay] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [views, setViews] = useState<Record<number, { s: number; e: number }>>({});
  const [menu, setMenu] = useState(false);
  const [newWeek, setNewWeek] = useState<{ target: string } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [review, setReview] = useState(false);
  const [err, setErr] = useState('');
  const [toast, showToast] = useToast();
  const navSlot = useNavSlot();

  const loadWeeks = useCallback(async () => {
    const w = await get<WeeksResp>(`/api/schedule/weeks?location=${locationId}`);
    setWeeks(w);
    return w;
  }, [locationId]);
  const loadWeek = useCallback(async (st: string, keepDay = false, pickDay?: number) => {
    try {
      const d = await get<WeekResp>(`/api/schedule/week?location=${locationId}&start=${st}`);
      setData(d); setStart(st); setErr('');
      if (pickDay !== undefined) { setDay(pickDay); setViews({}); }
      else if (!keepDay) {
        const t = d.days.findIndex((x) => x.date === d.today);
        setDay(t >= 0 ? t : 0); setViews({});
      }
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Can’t load the schedule.'); }
  }, [locationId]);
  // owner 11/1: "a way to pull up any day from the past to be able to review it": open the week of any date, on that day
  const goToDay = (iso: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return;
    const dow = new Date(iso + 'T12:00:00Z').getUTCDay();
    setMenu(false);
    void loadWeek(isoAdd(iso, -dow), false, dow);
  };
  useEffect(() => {
    setData(null); setWeeks(null);
    void loadWeeks().then((w) => {
      // open this week if it's started, else the last week started, else this (empty) week
      const st = w.weeks.some((x) => x.weekStart === w.thisWeek) ? w.thisWeek : (w.weeks.at(-1)?.weekStart ?? w.thisWeek);
      return loadWeek(st);
    }).catch(() => setErr('Can’t load the schedule.'));
  }, [loadWeeks, loadWeek]);

  // the week list opens scrolled to the week on screen (it grows by a week every week)
  useEffect(() => { if (menu) requestAnimationFrame(() => document.querySelector('.wkmenu .wkitem.on')?.scrollIntoView({ block: 'nearest' })); }, [menu]);
  const ro = !data?.canEdit;
  const keys = weeks?.weeks.map((w) => w.weekStart) ?? [];
  const at = start ? keys.indexOf(start) : -1;

  const postWeek = () => data?.week && setConfirm({
    title: `Post the week ending ${data.weekEnd}?`,
    text: `Everyone at ${data.location.name} will be able to see their shifts for this week in the app. You can still make changes after posting; employees see them once you press “Post changes” (mid-day changes show right away).`,
    ok: 'Post week',
    run: async () => { await post(`/api/schedule/week/${data.week!.id}/post`); await loadWeeks(); await loadWeek(data.weekStart, true); showToast('Posted. Employees can see this week now.'); },
  });
  // owner 11/2: "If i hit post changes what happens? do i get to review": Post changes opens the list of what changed first
  const postChanges = () => { if (data?.week) setReview(true); };

  const weekBar = data && weeks && (
    <div className="weeknav">
      {data.chip.cls === 'unp' && data.week
        ? <button className="wkchip unp" title={`${data.chip.tip} Click to see exactly what changed.`} onClick={() => setReview(true)}>{data.chip.chip} · Review</button>
        : <span className={'wkchip' + (data.chip.cls ? ' ' + data.chip.cls : '')} title={data.chip.tip}>{data.chip.chip}</span>}
      {weeks.canEdit && data.week?.status === 'draft' && <button className="tbtn primary" onClick={postWeek}>Post week</button>}
      {weeks.canEdit && data.unposted.length > 0 && <button className="tbtn primary" onClick={postChanges} title={`Employees still see the posted schedule. Changed: ${data.unposted.map((u) => u.name).join(', ')}`}>Post changes</button>}
      <button className="btn2" disabled={at <= 0 && !(at < 0 && keys.length)} aria-label="Previous week"
        onClick={() => { if (at < 0 && keys.length) void loadWeek(keys.at(-1)!); else if (at > 0) void loadWeek(keys[at - 1]!); }}>‹</button>
      <span className="wkpick">
        <button className="wkbtn" onClick={() => setMenu((m) => !m)} aria-expanded={menu} aria-haspopup="true">
          <span className="wktxt">Week ending {weekEndShort(data.weekStart)}/{isoAdd(data.weekStart, 6).slice(0, 4)} · {data.location.name}</span><span className="wkarr" aria-hidden="true">▾</span></button>
        {menu && (
          <span className="wkmenu" role="menu">
            <span className="wkgo">
              <label>Go to a day <input type="date" aria-label="Go to a day" onChange={(e) => goToDay(e.target.value)} /></label>
              {data.today && <button className="linkbtn" onClick={() => goToDay(data.today)}>Back to today</button>}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#4A5561', padding: '4px 10px' }}>Weeks ending</span>
            {weeks.weeks.map((w) => (
              <button key={w.id} className={'wkitem' + (w.weekStart === start ? ' on' : '')} role="menuitem" onClick={() => { setMenu(false); if (w.weekStart !== start) void loadWeek(w.weekStart); }}>
                <span>{w.label}</span><span className={'wkchip' + (w.status === 'draft' ? ' draft' : '')}>{w.status === 'draft' ? 'Draft' : 'Posted'}</span></button>
            ))}
            {weeks.weeks.length === 0 && <span style={{ fontSize: 13, color: '#4A5561', padding: '4px 10px' }}>No weeks started yet.</span>}
            {weeks.canEdit && <button className="wkitem wkadd" role="menuitem" onClick={() => { setMenu(false); setNewWeek({ target: weeks.nextWeek }); }}>+ Start the week ending {weekEndShort(weeks.nextWeek)}…</button>}
          </span>
        )}
      </span>
      <button className="btn2" disabled={at < 0 || at >= keys.length - 1} aria-label="Next week" onClick={() => { if (at >= 0 && at < keys.length - 1) void loadWeek(keys[at + 1]!); }}>›</button>
    </div>
  );

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {navSlot && weekBar && createPortal(weekBar, navSlot)}
      {err && <div className="errbar">{err}</div>}
      {data && !data.week && (
        <div className="comingsoon" style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
          <b style={{ fontSize: 16, color: '#17212B' }}>The week ending {data.weekEnd} hasn’t been started at {data.location.name} yet.</b>
          <span>Start it blank, or copy another week’s shifts. It stays a draft (employees can’t see it) until you press Post week.</span>
          {weeks?.canEdit && <button className="tbtn primary" onClick={() => setNewWeek({ target: data.weekStart })}>Start the week ending {data.weekEnd}…</button>}
        </div>
      )}
      {data && data.week && view === 'week' && <WeekSummary data={data} />}
      {data && data.week && (view === 'day' || view === 'rotation') && (
        <Board key={data.weekStart + '|' + day} rotation={view === 'rotation'} data={data} day={day} setDay={setDay} ro={ro} showAll={showAll} setShowAll={setShowAll}
          view={views[day]} setView={(v) => setViews((x) => ({ ...x, [day]: v }))}
          onDay={(patch) => setData((d) => d && { ...d, days: d.days.map((x, i) => (i === day ? { ...x, ...patch } : x)) })}
          onPresets={(presets) => setData((d) => d && { ...d, presets })}
          reload={() => loadWeek(data.weekStart, true)}
          onSaved={(chip, unposted) => setData((d) => d && { ...d, chip, unposted })}
          onRows={(rows, seed) => setData((d) => d && { ...d, days: d.days.map((x, i) => (i === day ? { ...x, rows, breakSeed: seed ?? x.breakSeed } : x)) })}
          showToast={showToast} />
      )}
      {newWeek && weeks && (
        <NewWeek locationId={locationId} locationName={data?.location.name ?? ''} target={newWeek.target} weeks={weeks}
          onClose={() => setNewWeek(null)}
          onDone={async () => { setNewWeek(null); await loadWeeks(); await loadWeek(newWeek.target); showToast(`The week ending ${weekEndShort(newWeek.target)} is started as a draft.`); }} />
      )}
      {review && data?.week && (
        <ReviewChanges weekId={data.week.id} canEdit={!!weeks?.canEdit} onClose={() => setReview(false)} showToast={showToast}
          onChanged={() => loadWeek(data.weekStart, true)} />
      )}
      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
      {toast}
      {me.role === 'hr' && null}
    </section>
  );
}

/** The week bar lives in the tab row at the top (as in the prototype). */
function useNavSlot() {
  const [el, setEl] = useState<HTMLElement | null>(null);
  useEffect(() => { setEl(document.getElementById('navslot')); }, []);
  return el;
}

function Board({ rotation, data, day, setDay, ro, showAll, setShowAll, view, setView, onDay, onPresets, reload, onSaved, onRows, showToast }: {
  rotation?: boolean; data: WeekResp; day: number; setDay: (d: number) => void; ro: boolean; showAll: boolean; setShowAll: (v: boolean) => void;
  view: { s: number; e: number } | undefined; setView: (v: { s: number; e: number }) => void;
  onDay: (patch: Partial<Day>) => void; onPresets: (p: Record<string, Needs>) => void; reload: () => Promise<void>;
  onSaved: (chip: WeekResp['chip'], unposted: WeekResp['unposted']) => void; onRows: (rows: Record<string, string>, seed?: number) => void;
  showToast: (m: string) => void;
}) {
  const [emailing, setEmailing] = useState(false);
  const d = data.days[day]!;
  const busy = useBusy(data.location.id, d.date);   // busy hours strip (owner 11/1)
  const people = data.people;
  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const [rows, setRows] = useState<Record<string, string>>(d.rows);
  const rowsRef = useRef(rows); rowsRef.current = rows;
  const saved = useRef<Record<string, string>>(d.rows);
  const seed = useRef(d.breakSeed);
  const paint = useRef<{ id: number; val: boolean; last: number } | null>(null);
  const frozen = useRef<number[] | null>(null);
  const [, force] = useState(0);
  const saving = useRef(Promise.resolve());
  const [xp, setXp] = useState<{ p: Person; step: 'ask' | 'pick' } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [copyFrom, setCopyFrom] = useState('');
  const [midOpen, setMidOpen] = useState<Change | true | false>(false);
  const [rotVer, setRotVer] = useState(0);
  const [lend, setLend] = useState<'lend' | 'office' | null>(null);
  // a reload from the server (e.g. after a mid-day change) brings the rows back in line
  useEffect(() => { if (!paint.current) { setRows(d.rows); rowsRef.current = d.rows; saved.current = { ...d.rows }; } }, [d.rows]);

  // ---- the kind of day, its switch hour and its "Custom" staffing numbers (saved per store and day)
  const saveDay = (patch: Partial<Pick<Day, 'traffic' | 'switchHours' | 'needCustom' | 'customOn'>>) => {
    onDay(patch);
    if (!data.week) return;
    void put(`/api/schedule/week/${data.week.id}/day/${day}/settings`, patch).catch((e) => showToast(e instanceof ApiError ? e.message : 'That didn’t save. Try again.'));
  };
  const switchHour = (t: string) => d.switchHours[t] ?? 17;
  const preset = data.presets[d.traffic] ?? { staff: {}, lead: {}, mgr: {} };
  const hasCustom = !!d.needCustom && (['staff', 'lead', 'mgr'] as const).some((k) => Object.keys(d.needCustom![k] ?? {}).length > 0);
  const setNeed = (kind: keyof Needs, H: number, val: string) => {
    let n: number | null = val.trim() === '' ? null : Math.max(0, Math.min(60, Math.round(Number(val))));
    if (n != null && isNaN(n)) n = null;
    let custom: Needs = d.needCustom ? JSON.parse(JSON.stringify(d.needCustom)) : { staff: {}, lead: {}, mgr: {} };
    if (!d.customOn) {   // start the custom set from what the board shows now
      custom = { staff: {}, lead: {}, mgr: {} };
      for (const k of ['staff', 'lead', 'mgr'] as const) for (let HH = FIRST_HOUR; HH < LAST_HOUR; HH++) custom[k][HH] = preset[k][HH] ?? null;
    }
    custom[kind][H] = n;
    saveDay({ needCustom: custom, customOn: true });
  };

  const rowOf = (id: number) => rows[id] ?? OFF;
  // break levels: Manager spot 7–9, Shift Lead spot 6, else level up to 5
  const levelOf = (p: Person) => breakLevel(p.level, p.canManagerSpot, p.canLeadSpot);

  const save = useCallback((changed: Record<string, string>, breakSeed?: number) => {
    if (!data.week || (!Object.keys(changed).length && !breakSeed)) return;
    saving.current = saving.current.then(async () => {
      try {
        const r = await put<{ chip: WeekResp['chip']; unposted: WeekResp['unposted'] }>(`/api/schedule/week/${data.week!.id}/day/${day}`, { rows: changed, ...(breakSeed ? { breakSeed } : {}) });
        Object.assign(saved.current, changed);
        onSaved(r.chip, r.unposted);
      } catch (e) {
        showToast(e instanceof ApiError ? e.message : 'That didn’t save. Check your connection and try again.');
        setRows({ ...saved.current }); rowsRef.current = { ...saved.current };
      }
    });
  }, [data.week, day, onSaved, showToast]);

  // Paints a range on one person's row, then places their breaks (everyone else keeps theirs).
  const setBlocks = (id: number, a0: number, b0: number, val: boolean) => {
    // only inside the day: not before the night before's overnight party ends, not after this day ends
    const lo = data.days[day]!.dayStart, hi = data.days[day]!.dayEnd - 1;
    const a = Math.max(lo, Math.min(hi, a0)), b = Math.max(lo, Math.min(hi, b0));
    const cur = rowsRef.current[id] ?? OFF;
    const next = paintRange(cur, a, b, val);
    if (next === cur) return;
    const ids = people.map((p) => p.id);
    const all = ids.map((x) => (x === id ? next : rowsRef.current[x] ?? OFF));
    const out = generateBreaks(all, people.map(levelOf), [ids.indexOf(id)], false, seed.current);
    const nr = { ...rowsRef.current };
    const r = out[ids.indexOf(id)]!;
    if (r === OFF) delete nr[id]; else nr[id] = r;
    rowsRef.current = nr; setRows(nr);
  };
  const finish = useCallback(() => {
    if (!paint.current) return;
    paint.current = null; frozen.current = null; force((x) => x + 1);
    const changed: Record<string, string> = {};
    const ids = new Set([...Object.keys(rowsRef.current), ...Object.keys(saved.current)]);
    for (const id of ids) { const now = rowsRef.current[id] ?? OFF; if (now !== (saved.current[id] ?? OFF)) changed[id] = now; }
    onRows(rowsRef.current);
    save(changed);
  }, [onRows, save]);
  useEffect(() => {
    window.addEventListener('pointerup', finish); window.addEventListener('pointercancel', finish);
    return () => { window.removeEventListener('pointerup', finish); window.removeEventListener('pointercancel', finish); };
  }, [finish]);

  const recalc = () => {
    const ids = people.map((p) => p.id);
    const res = recalcAll(ids.map((id) => rowsRef.current[id] ?? OFF), people.map(levelOf), seed.current);
    if (!res) { showToast('That’s already the best layout for today.'); return; }
    seed.current = res.usedSeed + 8;
    const nr: Record<string, string> = {}, changed: Record<string, string> = {};
    ids.forEach((id, i) => { const r = res.rows[i]!; if (r !== OFF) nr[id] = r; if (r !== (rowsRef.current[id] ?? OFF)) changed[id] = r; });
    rowsRef.current = nr; setRows(nr); onRows(nr, seed.current);
    save(changed, seed.current);
  };

  // "Copy from": this store's own people's shifts and breaks from another day of the week.
  const copyDay = (from: number) => {
    const src = data.days[from]!.rows;
    const nr: Record<string, string> = {}, changed: Record<string, string> = {};
    for (const p of people) {
      const cur = rowsRef.current[p.id] ?? OFF;
      const next = p.away || d.elsewhere[p.id] ? cur : src[p.id] ?? OFF;
      if (next !== OFF) nr[p.id] = next;
      if (next !== cur) changed[p.id] = next;
    }
    rowsRef.current = nr; setRows(nr); onRows(nr); save(changed); setCopyFrom('');
  };
  // The × → "Change employee": someone else takes the same shift and breaks; the first person comes off the day.
  const giveShift = (from: Person, to: Person) => {
    const row = rowsRef.current[from.id] ?? OFF;
    const nr = { ...rowsRef.current }; delete nr[from.id]; nr[to.id] = row;
    rowsRef.current = nr; setRows(nr); onRows(nr); save({ [from.id]: OFF, [to.id]: row }); setXp(null);
  };
  const removeFromDay = (p: Person) => {
    const nr = { ...rowsRef.current }; delete nr[p.id];
    rowsRef.current = nr; setRows(nr); onRows(nr); save({ [p.id]: OFF }); setXp(null);
  };

  // "Overnight party" (owner 10/23): tick it and pick the end (5:30–9:00 AM); the server checks nobody's in the way
  const setOvernight = async (end: number | null) => {
    try { await put(`/api/schedule/week/${data.week!.id}/day/${day}/overnight`, { end }); await reload(); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t save. Try again.'); }
  };
  const dayName = (i: number) => DAY_FULL[(i + 7) % 7];

  // Hours shown: the day's chosen window (default 11 AM – 10 PM), widened to include every shift.
  let spanS = 99, spanE = -1;
  for (const r of Object.values(rows)) for (let b = 0; b < r.length; b++) if (r[b] !== '.') {
    spanS = Math.min(spanS, FIRST_HOUR + Math.floor(b / 4)); spanE = Math.max(spanE, FIRST_HOUR + Math.ceil((b + 1) / 4));
  }
  const dStart = d.dayStart ?? 0, dEnd = d.dayEnd ?? DAY_BLOCKS;
  const dStartH = FIRST_HOUR + Math.floor(dStart / 4), dEndH = FIRST_HOUR + Math.ceil(dEnd / 4);
  const vw = view ?? { s: 11, e: 22 };
  const vS = Math.max(dStartH, Math.min(vw.s, spanS)), vE0 = Math.min(dEndH, Math.max(vw.e, spanE)), vE = vE0 > vS ? vE0 : Math.min(dEndH, vS + 1);
  const b0 = (vS - FIRST_HOUR) * 4, b1 = (vE - FIRST_HOUR) * 4, nCols = b1 - b0;
  const gcls = 'grow g' + nCols;
  const hours: number[] = []; for (let H = vS; H < vE; H++) hours.push(H);
  const busyHot = busyView(busy).hot;

  // Week hours (paid) per person, with today's live rows.
  const weekHours = new Map<number, { hrs: number; days: number }>();
  for (const p of people) {
    let hrs = 0, days = 0;
    data.days.forEach((x, i) => { const raw = x.sharedOut[p.id]?.blocks ?? (i === day ? rowOf(p.id) : x.rows[p.id] ?? OFF); const paid = summarize(x.sharedOut[p.id] ? raw : actualOf(x, p.id, raw)).paidHours; if (paid > 0) { hrs += paid; days++; } });
    weekHours.set(p.id, { hrs, days });
  }
  // Scheduled people A–Z, managers and shift leads (6–9) together at the bottom; "All" adds everyone else A–Z.
  const scheduled = people.filter((p) => (rows[p.id] ?? OFF) !== OFF);
  let order = scheduled.slice().sort((a, b) => (Number(a.level >= 6) - Number(b.level >= 6)) || a.name.localeCompare(b.name)).map((p) => p.id);
  // our people working at another store today: shown here in light blue, read-only (hours count here)
  // (never twice: for a moment after a reload a person can be in both lists)
  const outIds = people.filter((p) => d.sharedOut[p.id] && (rows[p.id] ?? OFF) === OFF).sort((a, b) => a.name.localeCompare(b.name)).map((p) => p.id);
  order = order.concat(outIds);
  const onBoard = new Set(order);
  if (showAll) order = order.concat(people.filter((p) => !onBoard.has(p.id) && !p.away && !d.elsewhere[p.id]).map((p) => p.id));
  if (paint.current && frozen.current) order = frozen.current; else if (!paint.current) frozen.current = null;
  const activePeople = people.filter((p) => !p.away);

  // what actually happens today: rows cut where someone left early or called out
  const actual: Record<string, string> = {};
  for (const [id, r] of Object.entries(rows)) actual[id] = actualOf(d, Number(id), r);
  const floor: number[] = [], onBreak: number[] = [];
  for (let b = b0; b < b1; b++) {
    let n = 0, nb = 0;
    for (const r of Object.values(actual)) { if (r[b] === 'W') n++; if (r[b] === 'B' || r[b] === 'L') nb++; }
    floor.push(n); onBreak.push(nb);
  }

  // "Staff / Leads / Managers needed": the preset for the day's kind, or the day's own Custom numbers.
  // Scheduled = the fewest on the clock (working or on break) in any quarter of the hour.
  const onClock = (H: number, test: (p: Person) => boolean) => {
    let lo = Infinity;
    for (let q = 0; q < 4; q++) {
      const bb = (H - FIRST_HOUR) * 4 + q; let c = 0;
      for (const p of people) { const r = actual[p.id]; if (r && r[bb] !== '.' && test(p)) c++; }
      lo = Math.min(lo, c);
    }
    return lo === Infinity ? 0 : lo;
  };
  const NEED_ROWS: [keyof Needs, string, (p: Person) => boolean, string][] = [
    ['staff', 'Staff needed', () => true, 'People'],
    ['lead', 'Leads needed', (p) => p.level === 6, 'Shift leads (level 6)'],
    ['mgr', 'Managers needed', (p) => p.level >= 7, 'Managers (levels 7–9)'],
  ];
  const needRo = ro;

  const cellAt = (x: number, y: number) => {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    if (!el?.dataset.emp) return null;
    return { id: Number(el.dataset.emp), b: Number(el.dataset.b) };
  };
  const syncScroll = (from: HTMLElement) => {
    const box = from.closest('.board'); if (!box) return;
    for (const sel of ['.sync-h', '.sync-b', '.sync-f', '.sync-p']) { const el = box.querySelector(sel) as HTMLElement | null; if (el && el !== from && el.scrollLeft !== from.scrollLeft) el.scrollLeft = from.scrollLeft; }
  };

  const dayPills = (
    <div className="noprint" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} role="group" aria-label="Day">
      {data.days.map((x, i) => <button key={x.date} className={'pill' + (i === day ? ' on' : '')} onClick={() => setDay(i)}>{x.label}</button>)}
    </div>
  );
  if (rotation) return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {dayPills}
      <RotationBoard key={rotVer} locationId={data.location.id} date={d.date} canMid={data.canMidDay} openMid={() => setMidOpen(true)} />
      {midOpen && <MidDay edit={midOpen === true ? undefined : midOpen} data={data} d={d} day={day} rows={rows} levels={people.map(levelOf)} seed={seed.current} byId={byId} setConfirm={setConfirm}
        onClose={() => setMidOpen(false)} reload={async () => { await reload(); setRotVer((v) => v + 1); }} showToast={showToast} />}
      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
    </section>
  );

  return (
    <section className="daysec" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* the day pills, with the Busy hours switch on the right (owner 11/1) */}
      <div className="noprint" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {dayPills}<span style={{ flex: '1 1 auto' }} /><BusyControls b={busy} />
      </div>
      <div className="noprint" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
        {TRAFFIC.map(([t, label]) => {
          const on = d.traffic === t, cls = on ? (d.customOn ? ' base' : ' on') : '';
          const pick = () => saveDay({ traffic: t, customOn: false });
          if (t === 'slow' || t === 'busy') return <button key={t} className={'pill' + cls} disabled={ro} onClick={pick}>{label}</button>;
          return (
            <span key={t} className={'pill pillsplit' + cls}>
              <button className="pillin" disabled={ro} onClick={pick}>{label}</button>
              <span className="pillsw">{t === 'slow_am_busy_pm' ? 'busy from' : 'slow from'}
                <select className="sel-xs pillsel" value={String(switchHour(t))} disabled={ro} aria-label={`${t === 'slow_am_busy_pm' ? 'Busy from' : 'Slow from'} (${label})`}
                  onChange={(e) => saveDay({ switchHours: { ...d.switchHours, [t]: Number(e.target.value) } })}>
                  {Array.from({ length: LAST_HOUR - FIRST_HOUR - 1 }, (_, i) => FIRST_HOUR + 1 + i).map((H) => <option key={H} value={H}>{hourLabel(H)}</option>)}
                </select></span>
            </span>
          );
        })}
        <button className="pill psbtn" onClick={() => setPresetsOpen(true)}>Modify presets</button>
        {hasCustom && <button className={'pill ucbtn' + (d.customOn ? ' on' : '')} disabled={ro} aria-pressed={d.customOn} onClick={() => saveDay({ customOn: true })}
          title={d.customOn ? `This day uses its own staffing numbers. Busy hours still follow “${trName(d.traffic)}”.` : 'Go back to the numbers you set for this day'}>Use Custom</button>}
      </div>
      <div className="printonly" style={{ fontSize: 18, fontWeight: 700, color: '#000' }}>{data.location.name} · {DAY_FULL[day]} {md(d.date)}/{d.date.slice(0, 4)} · Day board</div>
      <div className="printonly" style={{ fontSize: 11, color: '#333', marginTop: -6 }}>{data.week?.status === 'draft' ? 'DRAFT, not posted yet · ' : `Posted ${(/\d+\/\d+/.exec(data.chip.chip) ?? [''])[0]} · `}Scheduled employees only · printed {new Date().toLocaleString([], { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
      <div className="noprint" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{DAY_FULL[day]} {md(d.date)} · day board</h1>
        <div style={{ fontSize: 14, color: '#4A5561' }}>{ro ? 'View only.' : 'Press and drag along a row to paint a shift; start on a filled block to erase. To add someone, switch the list to All and paint their row.'}</div>
      </div>
      <div className="noprint" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4A5561' }}>Copy from
          <select className="sel-sm" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} disabled={ro} aria-label="Copy another day's schedule">
            <option value="">Choose a day…</option>
            {data.days.map((x, i) => (i === day ? null : <option key={i} value={i}>{DAY_FULL[i]} {md(x.date)}</option>))}
          </select>
        </label>
        <button className="tbtn" disabled={ro || copyFrom === ''} onClick={() => {
          const f = Number(copyFrom);
          setConfirm({ title: `Copy ${DAY_FULL[f]} onto ${DAY_FULL[day]}?`, text: `${data.location.name}’s own employees get the same shifts and breaks as on ${DAY_FULL[f]} ${md(data.days[f]!.date)}. What’s on ${DAY_FULL[day]} now for them is replaced.`, ok: 'Copy onto this day', run: () => copyDay(f) });
        }}>Copy onto this day</button>
        <button className="tbtn primary" disabled={ro} onClick={recalc}>Recalculate all breaks</button>
        <button className="tbtn" disabled={!data.canMidDay} onClick={() => setMidOpen(true)} title={data.canMidDay ? 'Someone left early, called out, arrived late or got called in' : 'Mid-day changes need a manager (level 7–9)'}>Mid-day change…</button>
        <button className="tbtn" onClick={() => { try { window.print(); } catch { /* fine */ } }}>Print this day</button>
        <button className="tbtn" disabled={!data.canEdit} onClick={() => setEmailing(true)} title="Email this day (or chosen days) as a PDF">Email…</button>
        {emailing && <EmailPdf locationId={data.location.id} storeName={data.location.name} what="day" weekStart={data.weekStart} date={d.date} onClose={() => setEmailing(false)} />}
        <button className="tbtn" disabled={ro || !data.otherStores.length} onClick={() => setLend('lend')} title="One of your people works at another store this day">Schedule at another store…</button>
        {data.canEditOffice && <button className="tbtn" onClick={() => setLend('office')} title="Admins only: a tech or office person fills in at this store">Add from Operations…</button>}
      </div>
      {outIds.map((id) => {
        const p = byId.get(id)!, o = d.sharedOut[id]!;
        return (
          <div key={id} className="noprint sharebar"><span className="sharedot" /><span>{p.name} → {o.store}, {summarize(o.blocks).shift} · hours count here</span>
            <button className="btn2" disabled={ro} aria-label={`Bring ${p.name} back to ${data.location.name}`} style={{ minHeight: 28, padding: '2px 10px' }}
              onClick={() => setConfirm({ title: `Bring ${p.name} back to ${data.location.name}?`, text: `${p.name}’s shift (${summarize(o.blocks).shift}) moves back to ${data.location.name}’s own schedule and comes off ${o.store}’s board.`, ok: 'Bring back',
                run: async () => { try { await post('/api/share/back', { employeeId: id, date: d.date }); await reload(); } catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t work. Try again.'); } } })}>Bring back</button></div>
        );
      })}
      {Object.entries(d.sharedIn).some(([, v]) => !v.office) && (
        <div className="noprint sharebar"><span className="sharedot" /><span>Shared to us today: {Object.entries(d.sharedIn).filter(([, v]) => !v.office).map(([id, v]) => `${byId.get(Number(id))?.name ?? ''} from ${v.from}`).join(' · ')} · their hours count at their home store</span></div>
      )}
      {Object.entries(d.sharedIn).some(([, v]) => v.office) && (
        <div className="noprint sharebar"><span className="sharedot" /><span>Filling in from Operations today: {Object.entries(d.sharedIn).filter(([, v]) => v.office).map(([id]) => { const p = byId.get(Number(id)); return `${p?.name ?? ''}${p?.position ? ` (${p.position})` : ''}`; }).join(' · ')} · only an admin can change their hours</span></div>
      )}
      {d.overnightEnd != null && <div className="noprint sharebar ovnbar"><span className="ovndot" /><span>Overnight party until {tlabel(d.overnightEnd)}: this day runs past 5:00 AM. All its hours count on this day, and the rotation, counter screen and phones stay on it until then.</span></div>}
      {d.dayStart > 0 && <div className="noprint sharebar ovnbar"><span className="ovndot" /><span>Overnight party from {dayName(day - 1)} until {tlabel(d.dayStart + DAY_BLOCKS)}: this day starts at {tlabel(d.dayStart + DAY_BLOCKS)}.</span></div>}
      {d.changes.map((c) => (
        <div key={c.id} className="noprint chgbar"><span className="chgdot" /><span>{changeText(c, d, byId)}</span><span style={{ flex: '1 1 auto' }} />
          <button className="barbtn" disabled={!data.canFix} title={data.canFix ? '' : 'Only a level 9 manager or an admin can edit or undo a change.'} onClick={() => setMidOpen(c)}>Edit</button>
          <button className="barbtn" disabled={!data.canFix} title={data.canFix ? '' : 'Only a level 9 manager or an admin can edit or undo a change.'} onClick={() => setConfirm({
            title: c.kind === 'called_in' ? 'Undo called-in shift?' : c.kind === 'called_out' ? 'Undo call-out?' : c.kind === 'arrived_late' ? 'Undo arriving late?' : 'Undo leaving early?',
            text: c.kind === 'called_in' ? `${byId.get(c.employeeId)?.name}’s ${tlabel(c.fromBlock ?? 0)}–${tlabel(c.toBlock ?? 0)} shift will be removed from the day board.`
              : c.kind === 'arrived_late' ? `${byId.get(c.employeeId)?.name}’s shift goes back to starting at ${tlabel(Math.max(0, (d.rows[c.employeeId] ?? OFF).search(/[^.]/)))} on the day board.`
              : `${byId.get(c.employeeId)?.name}’s full shift (${summarize(d.rows[c.employeeId] ?? OFF).paidHours} h) comes back on the day board, and their row is unlocked.${c.sickHours ? ' The sick pay is taken off the payroll list.' : ''}`,
            ok: 'Undo', run: async () => { try { await post(`/api/schedule/changes/${c.id}/undo`); await reload(); } catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t work.'); } },
          })}>Undo</button>
        </div>
      ))}
      <div className="noprint" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <b style={{ color: '#17212B', fontWeight: 600 }}>Showing {hourLabel(vS)} – {hourLabel(vE)}</b>
        <button className="tbtn" disabled={vS <= dStartH} onClick={() => setView({ s: Math.max(dStartH, vS - 2), e: vE - 2 >= spanE ? vE - 2 : vE })}>← Earlier</button>
        <button className="tbtn" disabled={vE >= dEndH} onClick={() => setView({ s: vS + 2 <= spanS ? vS + 2 : vS, e: Math.min(dEndH, vE + 2) })}>Later →</button>
        <button className="tbtn" onClick={() => setView(spanE > 0 ? { s: spanS, e: spanE } : { s: 11, e: 22 })}>Fit to schedule</button>
        {/* "Overnight party" (owner 10/23): an all-night lock-in; this day runs past 5 AM, up to 9 AM */}
        <label className="allday ovnbox" style={{ marginLeft: 6 }} title="For an all-night lock-in: this day runs past 5 AM (up to 9 AM). The next morning’s early hours are kept free for it.">
          <input type="checkbox" checked={d.overnightEnd != null} disabled={ro} onChange={(e) => void setOvernight(e.target.checked ? DAY_BLOCKS + 8 : null)} />Overnight party</label>
        {d.overnightEnd != null && <span className="ovnend">until
          <select className="sel-sm" value={d.overnightEnd} disabled={ro} onChange={(e) => void setOvernight(Number(e.target.value))} aria-label="Overnight party ends at">
            {OVERNIGHT_ENDS.map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}
          </select></span>}
      </div>
      <div className="scrollbox board" role="region" aria-label="Day board">
        <div className="stt" style={{ background: '#F7F8F5', borderRadius: '8px 8px 0 0' }}>
          <div className="hclip sync-h"><div className="wide">
            <div className={gcls} style={{ background: '#F7F8F5', borderBottom: '1px solid #C9CEC6' }}>
              <div className="stl alt" style={{ display: 'flex', alignItems: 'center', padding: '4px 6px' }}>
                <span className="printonly" style={{ fontSize: 12, fontWeight: 600, color: '#000' }}>Employee · shift</span>
                <span className="noprint" role="group" aria-label="Which employees to list" style={{ display: 'flex', flex: '1 1 auto', border: '1px solid #C9CEC6', borderRadius: 6, overflow: 'hidden' }}>
                  <button className={'seg' + (showAll ? '' : ' on')} onClick={() => setShowAll(false)} aria-pressed={!showAll}>Sched {scheduled.length}</button>
                  <button className={'seg' + (showAll ? ' on' : '')} onClick={() => setShowAll(true)} aria-pressed={showAll}>All {activePeople.length}</button>
                </span>
              </div>
              {hours.map((H) => <div key={H} className={'hcell' + (busyHot(H) ? ' busyhot' : '')}>{hourLabel(H)}</div>)}
              <div style={{ fontSize: 12, fontWeight: 600, color: '#4A5561', padding: '6px 8px', borderLeft: '1px solid #9AA39A' }}>Paid hours · breaks</div>
            </div>
            <BusyStrip b={busy} gcls={gcls} hours={hours} />
            {NEED_ROWS.map(([kind, label, test, who]) => (
              <div key={kind} className={gcls + ' noprint needrow'}>
                <div className="stl alt needlbl">{label}</div>
                {hours.map((H) => {
                  const pre = preset[kind][H] ?? null;
                  const want = d.customOn ? (d.needCustom?.[kind]?.[H] ?? null) : pre;
                  const own = d.customOn && want !== pre ? want : null;
                  const have = onClock(H, test);
                  const inCls = d.customOn ? (own != null || (want == null && pre != null) ? 'needin own' : 'needin cust') : 'needin';
                  const haveCls = want == null ? 'nhave' : have < want ? 'nhave under' : have > want ? 'nhave over' : 'nhave ok';
                  return (
                    <div key={H} className="ncell" title={`${who} scheduled ${hourLabel(H)}: ${have}${want == null ? '' : ` of ${want} needed`}${d.customOn ? ' (custom numbers for this day)' : want != null ? ' (from the preset)' : ''}. Counted as the fewest on the clock in any quarter of the hour.`}>
                      <NeedInput cls={inCls} value={want} disabled={needRo} aria={`${label} at ${hourLabel(H)}`} onSave={(v) => setNeed(kind, H, v)} />
                      <span className={haveCls}>{have}</span>
                    </div>
                  );
                })}
                <div className="needkey">box = needed · number = scheduled</div>
              </div>
            ))}
          </div></div>
        </div>
        <div className="hscroll sync-b" onScroll={(e) => syncScroll(e.currentTarget)}
          onPointerMove={(ev) => {
            const p = paint.current; if (!p) return;
            const c = cellAt(ev.clientX, ev.clientY);
            if (!c || c.id !== p.id || c.b === p.last) return;
            setBlocks(p.id, p.last, c.b, p.val); p.last = c.b;
          }}>
          <div className="wide" style={{ userSelect: 'none', WebkitUserSelect: 'none', touchAction: ro ? undefined : 'none' }}>
            {order.length === 0 && <div style={{ padding: '14px 12px', fontSize: 14, color: '#4A5561' }}>No one is scheduled this day yet. Switch the list to <b>All</b> and paint someone’s row.</div>}
            {order.map((id, pos) => {
              const p = byId.get(id); if (!p) return null;
              const out2 = d.sharedOut[id], inn = d.sharedIn[id];
              const row = out2 ? out2.blocks : rowOf(id), lf = out2 ? null : leftOf(d, id), lb = lf?.b ?? null, lt = out2 ? null : lateOf(d, id), la = lt?.b ?? null;
              const act = out2 ? row : actualRow(row, lb, la);
              // shared rows: someone from Operations can only be changed by an admin (breaks move on Recalculate)
              const rowRo = ro || !!out2 || (!!inn?.office && !data.canEditOffice);
              const sched = summarize(row), sm = summarize(act), wh = weekHours.get(id)!;
              const av = p.avail[new Date(d.date + 'T12:00:00Z').getUTCDay()] ?? '';
              const toff = p.timeOff[d.date];
              const offAt = (b: number) => !!toff && (toff.allDay || (b >= (toff.fromBlock ?? 0) && b < (toff.toBlock ?? 0)));
              const called = d.changes.some((c) => c.employeeId === id && c.kind === 'called_in');
              const out = lb != null && sm.paidHours === 0;
              const locked = lb != null || la != null || rowRo;
              const over = wh.hrs > 40 ? ' · over 40 hours' : p.maxHours != null && wh.hrs > p.maxHours ? ` · over max of ${p.maxHours}h` : p.maxDays != null && wh.days > p.maxDays ? ` · over max of ${p.maxDays} day${p.maxDays === 1 ? '' : 's'}` : '';
              const meta = out2 ? `@ ${out2.store}` : inn ? `From ${inn.from}` : lb != null || la != null ? [la != null ? `Late ${tlabel(la)}` : '', lb != null ? (out ? 'Called out' : `Left ${tlabel(lb)}`) : ''].filter(Boolean).join(' · ') : toff ? (toff.allDay ? 'Time off' : `Off ${tlabel(toff.fromBlock ?? 0)}–${tlabel(toff.toBlock ?? 0)}`) : called ? 'Called in' : `L${p.level} · ${wh.hrs}h wk${over}`;
              const metaCls = out2 || inn ? 'meta shared' : lb != null || la != null ? 'meta warn' : toff ? 'meta toffm' : called ? 'meta callin' : wh.hrs > 40 ? 'meta warn' : over ? 'meta maxw' : 'meta';
              const why = lf?.c.reason ? REASONS[lf.c.reason] : '';
              const lwhy = lt?.c.reason ? REASONS[lt.c.reason] : '';
              const brk = [la != null ? 'Late' + (lwhy ? ` (${lwhy.toLowerCase()})` : '') : '', lb != null ? why : '', ...sm.breaks].filter(Boolean).join(' · ');
              const tip = out2 ? `Working at ${out2.store} today; hours count here. ${out2.store}’s manager handles their breaks and rotation.` : inn ? (inn.office ? `From ${inn.from}: only an admin can change their hours; you can move their breaks.` : `Shared from ${inn.from}: hours count at ${inn.from}.`) : lb != null || la != null ? [la != null ? `Arrived at ${tlabel(la)}${lwhy ? ` · reason: ${lwhy}` : ''}` : '', lb != null ? `Left at ${tlabel(lb)}${why ? ` · reason: ${why}` : ''}` : ''].filter(Boolean).join('. ') : toff ? `Approved time off${toff.allDay ? ' (all day)' : ` ${tlabel(toff.fromBlock ?? 0)}–${tlabel(toff.toBlock ?? 0)}`}${toff.note ? `: ${toff.note}` : ''}` : '';
              return (
                <div key={id} className={gcls + (sched.paidHours > 0 ? '' : ' offrow')}>
                  <div className={p.level >= 6 ? 'stl namecell leadcell' : 'stl namecell'} style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '2px 2px 2px 8px', borderBottom: '1px solid #E1E4DE', minHeight: 34, boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: '1 1 auto' }}>
                      <span className={onBoard.has(id) ? 'empname' : 'empname off'} title={p.name} style={{ fontSize: 13 }}>{p.short}</span>
                      <span className={metaCls} title={tip}>{meta}</span>
                    </div>
                    <span className={sched.paidHours > 0 ? 'shiftcol' : 'shiftcol off'}>{sched.paidHours > 0 ? sched.shift.split('–')[0] + '\n' + sched.shift.split('–').pop() : 'Off'}</span>
                    <span className="ponly shiftline">{sched.paidHours > 0 ? sched.shift : ''}</span>
                    <button className="xbtn" disabled={ro || !onBoard.has(id) || locked} aria-label={`Remove ${p.name} from ${DAY_FULL[day]}${inn && !inn.office ? ` (ends the share with ${inn.from})` : ''}`} onClick={() => setXp({ p, step: 'ask' })}>×</button>
                  </div>
                  {Array.from({ length: nCols }, (_, j) => {
                    const b = b0 + j, k = KIND[row[b]!] ?? '';
                    // scheduled when not available = red line; scheduled during APPROVED TIME OFF = purple line (owner 10/21)
                    const badOff = !!k && offAt(b), bad = badOff || (!!k && av[b] === 'N');
                    const cls = 'cell' + (b % 4 === 0 ? ' hour' : '') + (pos % 2 ? ' zebra' : '') + (k ? ' ' + k : '') + (k && (out2 || inn) ? ' loan' : '') + (bad ? ' conflict' + (badOff ? ' toffc' : '') : '')
                      + (!k && offAt(b) ? ' toff' : !k && av[b] === 'N' ? ' avN' : !k && av[b] === 'P' ? ' avP' : '')
                      + (k && lb != null && b >= lb ? ' gone' + (b === lb && !out ? ' leftmark' : '') : '') + (k && la != null && b < la ? ' gone' : '') + (k && la != null && b === la ? ' leftmark' : '') + (locked ? ' locked' : '');
                    // outside the day: after it ends, or still the night before's overnight party (owner 10/23)
                    const outside = b >= dEnd || b < dStart;
                    return (
                      <button key={b} className={cls + (outside ? ' nightoff' : '')} disabled={ro || locked || outside} data-emp={out2 ? undefined : id} data-b={b}
                        aria-label={`${p.name} ${tlabel(b)} ${k === 'R' ? 'break' : k === 'M' ? 'lunch' : k ? 'working' : 'off'}`}
                        onPointerDown={(ev) => {
                          if (ro || locked || outside || (ev.button !== undefined && ev.button !== 0)) return;
                          ev.preventDefault(); (ev.target as HTMLElement).releasePointerCapture?.(ev.pointerId);
                          frozen.current = order.slice();
                          paint.current = { id, val: row[b] === '.', last: b };
                          setBlocks(id, b, b, row[b] === '.');
                        }}
                        onKeyDown={(ev) => { if (!ro && (ev.key === 'Enter' || ev.key === ' ')) { ev.preventDefault(); paint.current = { id, val: row[b] === '.', last: b }; setBlocks(id, b, b, row[b] === '.'); finish(); } }} />
                    );
                  })}
                  <div style={{ padding: '3px 8px', borderLeft: '1px solid #9AA39A', borderBottom: '1px solid #E1E4DE', display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1.25 }}>
                    <span className={lb != null ? 'paidl early' : 'paidl'} title={tip}>{lb != null ? `${sm.paidHours} h paid · ${sched.paidHours} sched` : sm.paidHours > 0 ? `${sm.paidHours} h paid` : ''}</span><span className={'bk bk' + String(fitSize(brk)).replace('.', '_')} title={brk}>{brk}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="stb" style={{ background: '#F7F8F5', borderRadius: '0 0 8px 8px' }}>
          <div className="hclip sync-f"><div className="wide">
            <div className={gcls} style={{ background: '#F7F8F5' }}>
              <div className="stl alt" style={{ fontSize: 12, fontWeight: 600, color: '#4A5561', padding: '6px 12px' }}>On the floor</div>
              {floor.map((n, i) => <div key={i} className={n <= 3 ? 'fcell low' : 'fcell'}>{n}</div>)}
              <div style={{ borderLeft: '1px solid #9AA39A' }} />
            </div>
            <div className={gcls} style={{ background: '#F7F8F5', borderTop: '1px solid #E1E4DE' }}>
              <div className="stl alt" style={{ fontSize: 12, fontWeight: 600, color: '#4A5561', padding: '6px 12px' }}>On break or lunch</div>
              {onBreak.map((n, i) => <div key={i} className={n >= 3 ? 'fcell low' : 'fcell'}>{n || ''}</div>)}
              <div style={{ borderLeft: '1px solid #9AA39A' }} />
            </div>
          </div></div>
          <div className="proxybar sync-p" onScroll={(e) => syncScroll(e.currentTarget)} aria-label="Scroll the day board sideways"><div className="wide"><div className={gcls} style={{ height: 1 }} /></div></div>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px', fontSize: 13, color: '#4A5561' }}>
        <span><span className="swatch" style={{ background: '#2DC369' }} />Working</span>
        <span><span className="swatch" style={{ background: '#FFF27A' }} />15-min break</span>
        <span><span className="swatch" style={{ background: '#69D8F0' }} />30-min lunch</span>
        <span className="noprint"><span className="swatch" style={{ background: '#CDEFD9', border: '1px solid #9AA39A' }} />Prefers to work</span>
        <span><span className="swatch" style={{ background: 'repeating-linear-gradient(135deg,#BFEBD0,#BFEBD0 4px,#FFFFFF 4px,#FFFFFF 7px)', border: '1px solid #9AA39A' }} />Scheduled, not worked (left early)</span>
        <span><span className="swatch" style={{ background: '#8EC5F2' }} />Shared between stores</span>
        <span className="noprint"><span className="swatch" style={{ background: 'repeating-linear-gradient(135deg,#F9D7D3,#F9D7D3 3px,#FFFFFF 3px,#FFFFFF 6px)', border: '1px solid #9AA39A' }} />Not available</span>
        <span><span className="swatch" style={{ background: 'repeating-linear-gradient(135deg,#E2D4F3,#E2D4F3 3px,#FFFFFF 3px,#FFFFFF 6px)', border: '1px solid #9AA39A' }} />Approved time off</span>
        <span className="noprint"><span className="swatch" style={{ background: '#2DC369', boxShadow: 'inset 0 -3px 0 #B42318' }} />Scheduled when not available</span>
        <span className="noprint"><span className="swatch" style={{ background: '#2DC369', boxShadow: 'inset 0 -3px 0 #7B3FC4' }} />Scheduled during approved time off</span>
      </div>
      {xp && <XPop xp={xp} setXp={setXp} day={day} d={d} data={data} rows={rows} weekHours={weekHours} setConfirm={setConfirm} onDelete={removeFromDay} onGive={giveShift} />}
      {lend && <LendModal mode={lend} data={data} d={d} day={day} weekHours={weekHours} onClose={() => setLend(null)} setConfirm={setConfirm}
        onDone={async () => { setLend(null); await reload(); }} showToast={showToast} />}
      {presetsOpen && <Presets data={data} traffic={d.traffic} onClose={() => setPresetsOpen(false)} onPresets={onPresets} showToast={showToast} setConfirm={setConfirm} />}
      {midOpen && <MidDay edit={midOpen === true ? undefined : midOpen} data={data} d={d} day={day} rows={rows} levels={people.map(levelOf)} seed={seed.current} byId={byId} setConfirm={setConfirm}
        onClose={() => setMidOpen(false)} reload={reload} showToast={showToast} />}
      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
    </section>
  );
}

function NewWeek({ locationId, locationName, target, weeks, onClose, onDone }: {
  locationId: number; locationName: string; target: string; weeks: WeeksResp; onClose: () => void; onDone: () => Promise<void>;
}) {
  const srcs = weeks.weeks.slice().reverse();
  const [mode, setMode] = useState<'all' | 'days' | 'blank'>(srcs.length ? 'all' : 'blank');
  const [src, setSrc] = useState(srcs[0]?.weekStart ?? '');
  const [days, setDays] = useState<Record<number, boolean>>({ 0: true, 1: true, 2: true, 3: true, 4: true, 5: true, 6: true });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const n = Object.values(days).filter(Boolean).length;
  return (
    <Modal title={`Start the week ending ${weekEndShort(target)} · ${locationName}`} onClose={onClose}>
      <div className="aechoices" role="group" aria-label="How to start the week">
        {([['all', 'Copy a whole week'], ['days', 'Choose days to copy'], ['blank', 'Start blank']] as const).map(([m, label]) => (
          <button key={m} className={'aechoice' + (mode === m ? ' on' : '')} aria-pressed={mode === m} disabled={m !== 'blank' && !srcs.length} onClick={() => setMode(m)}>{label}</button>
        ))}
      </div>
      {mode !== 'blank' && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Copy from
          <select className="sel-sm" value={src} onChange={(e) => setSrc(e.target.value)} aria-label="Week to copy from">
            {srcs.map((w) => <option key={w.id} value={w.weekStart}>Week ending {weekEndShort(w.weekStart)}</option>)}
          </select>
        </label>
      )}
      {mode === 'days' && (
        <div className="nwdays">{DAY_FULL.map((dn, i) => (
          <label key={i} className="allday"><input type="checkbox" checked={!!days[i]} onChange={() => setDays((x) => ({ ...x, [i]: !x[i] }))} />{dn} {md(isoAdd(src, i))} → {md(isoAdd(target, i))}</label>
        ))}</div>
      )}
      <div style={{ fontSize: 13, lineHeight: 1.5, color: '#4A5561' }}>
        {mode === 'blank' ? 'The week starts empty; paint shifts on each day. '
          : `Copies ${locationName}’s own employees’ shifts and breaks${mode === 'days' ? ' for the days you tick' : ' for all seven days'}, on the same weekdays. `}
        The new week is a draft: employees can’t see it until you post it.
      </div>
      {err && <div className="fieldnote bad">{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>Cancel</button>
        <button className="tbtn primary" style={{ minWidth: 120 }} disabled={busy || (mode === 'days' && n === 0)} onClick={async () => {
          setBusy(true); setErr('');
          try {
            await post('/api/schedule/weeks', { locationId, weekStart: target, mode, ...(mode === 'blank' ? {} : { from: src }), ...(mode === 'days' ? { days: Object.keys(days).filter((k) => days[Number(k)]).map(Number) } : {}) });
            await onDone();
          } catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t work. Try again.'); } finally { setBusy(false); }
        }}>Create week</button>
      </div>
    </Modal>
  );
}

/** A "needed" box: saves when you leave it or press Enter (so typing "12" doesn't save "1" first). */
function NeedInput({ cls, value, disabled, aria, onSave }: { cls: string; value: number | null; disabled: boolean; aria: string; onSave: (v: string) => void }) {
  const [v, setV] = useState(value == null ? '' : String(value));
  useEffect(() => { setV(value == null ? '' : String(value)); }, [value]);
  const commit = () => { if (v !== (value == null ? '' : String(value))) onSave(v); };
  return (
    <input className={cls} type="number" min={0} max={60} inputMode="numeric" value={v} disabled={disabled} aria-label={aria} placeholder="–"
      onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
  );
}

/** The × on a row: "Delete employee and shift", "Change employee" or Cancel (CLAUDE.md "Day board x"). */
function XPop({ xp, setXp, day, d, data, rows, weekHours, setConfirm, onDelete, onGive }: {
  xp: { p: Person; step: 'ask' | 'pick' }; setXp: (x: { p: Person; step: 'ask' | 'pick' } | null) => void; day: number; d: Day; data: WeekResp;
  rows: Record<string, string>; weekHours: Map<number, { hrs: number; days: number }>; setConfirm: (c: ConfirmSpec) => void;
  onDelete: (p: Person) => void; onGive: (from: Person, to: Person) => void;
}) {
  const from = xp.p, row = rows[from.id] ?? OFF, sm = summarize(row);
  const first = from.name.split(' ')[0];
  const weekdayIdx = new Date(d.date + 'T12:00:00Z').getUTCDay();
  // This store's people with no shift that day (time off is checked once time off is built)
  const cands = data.people.filter((p) => !p.away && p.id !== from.id && !rows[p.id] && !d.elsewhere[p.id] && !p.timeOff[d.date]?.allDay).map((p) => {
    const av = p.avail[weekdayIdx] ?? '', to = p.timeOff[d.date];
    let nB = 0, pB = 0, wB = 0, a = -1, z = -1;
    for (let b = 0; b < row.length; b++) if (row[b] !== '.') { wB++; if (av[b] === 'N' || (to && b >= (to.fromBlock ?? 0) && b < (to.toBlock ?? 0))) { nB++; if (a < 0) a = b; z = b + 1; } if (av[b] === 'P') pB++; }
    const wl = weekHours.get(p.id) ?? { hrs: 0, days: 0 }, after = wl.hrs + sm.paidHours, warns: string[] = [];
    if (nB) warns.push(`${to ? 'Time off or not available' : 'Not available'} ${tlabel(a)}–${tlabel(z)}`);
    if (p.maxHours != null && after > p.maxHours) warns.push(`Goes over their ${p.maxHours} h limit`);
    if (p.maxDays != null && wl.days + 1 > p.maxDays) warns.push(`Goes over their ${p.maxDays}-day limit`);
    if (after > 40) warns.push('Over 40 h this week');
    if (from.level >= 6 && p.level < 6) warns.push('Not a lead or manager');
    return { p, sortA: nB ? 2 : warns.length ? 1 : 0, sortB: wl.hrs, warns,
      info: (wB && pB === wB ? 'Prefers these hours · ' : nB ? '' : 'Available · ') + `${wl.hrs} h this week → ${after} h`,
      cls: 'tachoice xpc' + (nB ? ' bad' : warns.length ? ' meh' : '') };
  }).sort((x, y) => x.sortA - y.sortA || x.sortB - y.sortB || x.p.name.localeCompare(y.p.name));
  return (
    <Modal title={xp.step === 'ask' ? `Remove ${from.name} from ${DAY_FULL[day]}?` : `Who takes ${first}’s shift?`} onClose={() => setXp(null)}>
      <div style={{ fontSize: 14, color: '#4A5561' }}>{DAY_FULL[day]} {md(d.date)} · {sm.paidHours > 0 ? `${sm.shift} · ${sm.paidHours} h paid` : 'no shift yet'}</div>
      {xp.step === 'ask' ? (
        <>
          <div className="tachoices">
            <button className="tachoice xdel" onClick={() => onDelete(from)}><b>Delete employee and shift</b>
              <span className="tadesc">{sm.paidHours > 0 ? `Takes ${first} and their ${sm.shift} shift off the day. Nobody replaces them.` : `Takes ${first} off the day board.`}</span></button>
            <button className="tachoice" onClick={() => setXp({ p: from, step: 'pick' })}><b>Change employee</b>
              <span className="tadesc">Give this shift to someone else. Pick from the people who aren’t working today.</span></button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="btn2" onClick={() => setXp(null)}>Cancel</button></div>
        </>
      ) : (
        <>
          <div className="xplist" role="list">
            {cands.map((c) => (
              <button key={c.p.id} className={c.cls} role="listitem" onClick={() => setConfirm({
                title: `Give the shift to ${c.p.name}?`,
                text: `${c.p.name} takes ${from.name}’s ${sm.paidHours > 0 ? `${sm.shift} shift` : 'spot'} on ${DAY_FULL[day]} ${md(d.date)}, with the same breaks. ${from.name} comes off the day.${c.warns.length ? ` Heads up: ${c.warns.join('; ')}.` : ''}`,
                ok: 'Give shift', run: () => onGive(from, c.p),
              })}>
                <b>{c.p.name} · L{c.p.level}</b><span className="tadesc">{c.info}</span>{c.warns.length > 0 && <span className="xpwarn">{c.warns.join(' · ')}</span>}
              </button>
            ))}
            {cands.length === 0 && <div className="msgnone">Everyone at {data.location.name} is already working or off today.</div>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <button className="btn2" onClick={() => setXp({ p: from, step: 'ask' })}>← Back</button>
            <button className="btn2" onClick={() => setXp(null)}>Cancel</button>
          </div>
        </>
      )}
    </Modal>
  );
}

/** "Modify presets": the store's normal numbers per hour (8 AM – midnight) for each kind of day. */
function Presets({ data, traffic, onClose, onPresets, showToast, setConfirm }: {
  data: WeekResp; traffic: string; onClose: () => void; onPresets: (p: Record<string, Needs>) => void;
  showToast: (m: string) => void; setConfirm: (c: ConfirmSpec) => void;
}) {
  const [tr, setTr] = useState(traffic);
  const ro = !data.canEditPresets;
  const cur = data.presets[tr] ?? { staff: {}, lead: {}, mgr: {} };
  const send = async (numbers: Needs, t = tr) => {
    try { onPresets((await put<{ presets: Record<string, Needs> }>('/api/schedule/presets', { locationId: data.location.id, traffic: t, numbers })).presets); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t save. Try again.'); }
  };
  const setCell = (kind: keyof Needs, H: number, v: string) => {
    const n = v.trim() === '' ? null : Math.max(0, Math.min(60, Math.round(Number(v))));
    const next: Needs = JSON.parse(JSON.stringify(cur));
    if (n == null || isNaN(n)) delete next[kind][H]; else next[kind][H] = n;
    void send(next);
  };
  const hrs = Array.from({ length: 17 }, (_, i) => 8 + i);
  return (
    <Modal title={`Staffing presets · ${data.location.name}`} onClose={onClose} wide>
      <div style={{ fontSize: 13, color: '#4A5561' }}>How many people you normally need each hour for each kind of day. The day board fills its “needed” boxes from the preset that matches the day; a number typed on the day board changes that day only.</div>
      <div className="aechoices pstabs" role="group" aria-label="Which kind of day">
        {TRAFFIC.map(([t, label]) => <button key={t} className={'aechoice' + (tr === t ? ' on' : '')} aria-pressed={tr === t} onClick={() => setTr(t)}>{label}{traffic === t ? ' · today' : ''}</button>)}
      </div>
      <div className="psscroll"><div className="psgrid" style={{ gridTemplateColumns: '130px repeat(17, minmax(44px, 1fr))' }}>
        <div className="pshead">Hour</div>{hrs.map((H) => <div key={H} className="pshead">{hourLabel(H)}</div>)}
        {([['staff', 'Staff needed'], ['lead', 'Leads needed'], ['mgr', 'Managers needed']] as [keyof Needs, string][]).map(([k, label]) => (
          <div key={k} style={{ display: 'contents' }}>
            <div className="pslbl">{label}</div>
            {hrs.map((H) => <div key={H} className="pscell"><NeedInput key={tr + k + H} cls="needin" value={cur[k][H] ?? null} disabled={ro} aria={`${label}, ${hourLabel(H)}`} onSave={(v) => setCell(k, H, v)} /></div>)}
          </div>
        ))}
      </div></div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4A5561' }}>Copy from
          <select className="sel-sm" value="" disabled={ro} aria-label="Copy another preset into this one" onChange={(e) => {
            const v = e.target.value; if (!v) return;
            setConfirm({ title: 'Copy preset?', text: `The numbers from “${trName(v)}” replace the ones in “${trName(tr)}” for ${data.location.name}.`, ok: 'Copy', run: () => send(data.presets[v] ?? { staff: {}, lead: {}, mgr: {} }) });
          }}>
            <option value="">choose…</option>
            {TRAFFIC.filter(([t]) => t !== tr).map(([t, label]) => <option key={t} value={t}>{label}</option>)}
          </select>
        </label>
        <span style={{ flex: '1 1 auto' }} />
        <span style={{ fontSize: 12, color: '#6B7682' }}>Saved as you type · leads = level 6, managers = levels 7–9</span>
        <button className="tbtn primary" style={{ minWidth: 90 }} onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}

/** Week summary (prototype "Week summary" tab): built from the seven day boards. */
function WeekSummary({ data }: { data: WeekResp }) {
  const [texting, setTexting] = useState<{ only?: number } | null>(null);
  const [emailing, setEmailing] = useState(false);
  const [textVer, setTextVer] = useState(0);
  const posted = data.week?.status === 'posted';
  const textLog = useTextLog(posted ? data.week!.id : null, textVer);
  const [sort, setSort] = useState<{ key: 'name' | 'days' | 'hours'; dir: 1 | -1 }>({ key: 'name', dir: 1 });
  const rows = data.people.filter((p) => !p.away).map((p) => {
    let count = 0, hrs = 0;
    const days = data.days.map((d) => {
      // shifts at another store count here too (hours count at the home store)
      const out = d.sharedOut[p.id], raw = out?.blocks ?? d.rows[p.id] ?? OFF;
      const sm = summarize(out ? raw : actualOf(d, p.id, raw));
      if (sm.paidHours > 0) { count++; hrs += sm.paidHours; }
      return sm.paidHours > 0 ? sm.shift.replace(' & ', '\n') + (out ? `\n@ ${out.store}` : '') : d.elsewhere[p.id] ? `@ ${d.elsewhere[p.id]}` : 'Off';
    });
    const overD = p.maxDays != null && count > p.maxDays, overH = (p.maxHours != null && hrs > p.maxHours) || hrs > 40;
    const limit = p.maxHours == null && p.maxDays == null ? 'No weekly limit set'
      : 'Wants at most ' + [p.maxHours != null ? `${p.maxHours} h` : '', p.maxDays != null ? `${p.maxDays} day${p.maxDays === 1 ? '' : 's'}` : ''].filter(Boolean).join(' and ') + ' a week';
    return { p, days, count, hrs, overD, overH, tip: limit + (hrs > 40 ? ' · Over 40 hours this week' : '') };
  }).sort((a, b) => {
    const c = sort.key === 'name' ? a.p.name.localeCompare(b.p.name) : sort.key === 'days' ? b.count - a.count : b.hrs - a.hrs;
    return (c * sort.dir) || a.p.name.localeCompare(b.p.name);
  });
  const head = (k: 'name' | 'days' | 'hours', label: string, tip: string) => {
    const on = sort.key === k;
    const arrow = on ? (sort.dir === 1 ? (k === 'name' ? ' ▲' : ' ▼') : (k === 'name' ? ' ▼' : ' ▲')) : '';
    return <button className="sortbtn" title={tip} onClick={() => setSort({ key: k, dir: on ? (-sort.dir as 1 | -1) : 1 })}>{label}<span className="sortarr">{arrow}</span></button>;
  };
  const end = isoAdd(data.weekStart, 6);
  return (
    <section className="weeksec" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px' }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Week summary · {data.location.name} · Sun {md(data.weekStart)} – Sat {md(end)}/{end.slice(0, 4)}</h1>
        <span className="printonly wkprintsub">{data.week?.status === 'draft' ? 'DRAFT, not posted yet' : `Posted ${(/\d+\/\d+/.exec(data.chip.chip) ?? [''])[0]}`} · printed {new Date().toLocaleString([], { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
        <span className="noprint" style={{ flex: '1 1 auto' }} />
        <button className="tbtn noprint" onClick={() => { try { window.print(); } catch { /* fine */ } }} title="Prints this table, portrait">Print week</button>
        <button className="tbtn noprint" disabled={!data.canEdit} onClick={() => setEmailing(true)} title="Email this week summary (or several weeks) as a PDF">Email…</button>
        {emailing && <EmailPdf locationId={data.location.id} storeName={data.location.name} what="week" weekStart={data.weekStart} onClose={() => setEmailing(false)} />}
        <button className="tbtn primary noprint" disabled={!posted} onClick={() => setTexting({})} title={posted ? 'Text employees their posted schedule' : 'Post the week first; then you can text it'}>Text schedules…</button>
      </div>
      {textLog.map((l, i) => <div key={i} className="sharebar noprint" style={{ background: '#EEF7EE', borderColor: '#BDE8CD' }}><span>✓ {l}</span></div>)}
      <div className="noprint" style={{ fontSize: 14, color: '#4A5561' }}>Built automatically from the seven day boards. Hours are paid hours (lunch excluded). “32 / 30” means scheduled / the most they want; red means over.</div>
      <div className="wkwrap" style={{ overflowX: 'auto', border: '1px solid #C9CEC6', borderRadius: 8 }}>
        <table className="tbl wktbl" style={{ minWidth: 1100 }}>
          <thead><tr>
            <th aria-sort={sort.key === 'name' ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>{head('name', 'Employee', 'Sort by name')}</th>
            {data.days.map((d) => <th key={d.date}>{d.label}</th>)}
            <th className="noprint">{head('days', 'Days', 'Days worked / most they want · click to sort')}</th>
            <th className="noprint">{head('hours', 'Hours', 'Paid hours / most they want · click to sort')}</th>
            <th className="noprint" aria-label="Text" />
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.p.id}>
                <td style={{ fontWeight: 500 }}>{r.p.name}</td>
                {r.days.map((t, i) => <td key={i} className={t === 'Off' ? 'mono offday' : t.includes('@') ? 'mono awayday' : 'mono'} style={{ whiteSpace: 'pre-line' }}>{t}</td>)}
                <td className={'noprint ' + (r.overD ? 'mono overmax' : 'mono')} title={r.tip}>{r.count}{r.p.maxDays != null ? ` / ${r.p.maxDays}` : ''}</td>
                <td className={'noprint ' + (r.overH ? 'mono overmax' : 'mono')} title={r.tip}>{r.hrs}{r.p.maxHours != null ? ` / ${r.p.maxHours}` : ''}</td>
                <td className="noprint"><button className="btn2" disabled={!posted} onClick={() => setTexting({ only: r.p.id })} aria-label={`Text ${r.p.name} their schedule`}
                  title={posted ? `Text ${r.p.name} their posted schedule` : 'Post the week first'} style={{ minHeight: 28, padding: '2px 10px' }}>Text</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {texting && data.week && <TextSchedules weekId={data.week.id} storeName={data.location.name} only={texting.only} onClose={() => { setTexting(null); setTextVer((v) => v + 1); }} />}
    </section>
  );
}

/** "Taylor Wynn left at 3:00p (sick) · 4 h paid of 7.5 scheduled · 3.5 h sick pay recorded for payroll" */
function changeText(c: Change, d: Day, byId: Map<number, Person>): string {
  const name = byId.get(c.employeeId)?.name ?? 'Someone';
  if (c.kind === 'called_in') return `${name} called in, ${tlabel(c.fromBlock ?? 0)}–${tlabel(c.toBlock ?? 0)}`;
  const row = d.rows[c.employeeId] ?? OFF, sc = summarize(row).paidHours, ac = summarize(actualOf(d, c.employeeId, row)).paidHours;
  if (c.kind === 'arrived_late') return `${name} arrived at ${tlabel(c.atBlock ?? 0)}${c.reason ? ` (${REASONS[c.reason]!.toLowerCase()})` : ''} · ${ac} h paid of ${sc} scheduled`;
  const lf = leftOf(d, c.employeeId)!;
  return `${name}${c.kind === 'called_out' ? ' called out' : ` left at ${tlabel(lf.b)}`}${c.reason ? ` (${REASONS[c.reason]!.toLowerCase()})` : ''} · ${ac} h paid of ${sc} scheduled`
    + (c.sickHours ? ` · ${c.sickHours} h sick pay recorded for payroll` : '');
}

/** "Mid-day changes · Wednesday 10/21": left early (or called out, All day), called in. */
function MidDay({ edit, data, d, day, rows, levels, seed, byId, setConfirm, onClose, reload, showToast, counter }: {
  counter?: boolean; edit?: Change; data: WeekResp; d: Day; day: number; rows: Record<string, string>; levels: number[]; seed: number; byId: Map<number, Person>;
  setConfirm: (c: ConfirmSpec) => void; onClose: () => void; reload: () => Promise<void>; showToast: (m: string) => void;
}) {
  // "Edit" on a change bar opens this with that change filled in
  const el = edit && (edit.kind === 'left_early' || edit.kind === 'called_out') ? edit : null, ei = edit && edit.kind === 'called_in' ? edit : null;
  const elt = edit && edit.kind === 'arrived_late' ? edit : null;
  const [lateId, setLateId] = useState(elt ? String(elt.employeeId) : ''); const [lateAt, setLateAt] = useState(elt?.atBlock != null ? String(elt.atBlock) : '');
  const [lateWhy, setLateWhy] = useState(elt?.reason ?? '');
  const [leftId, setLeftId] = useState(el ? String(el.employeeId) : ''); const [allDay, setAllDay] = useState(el?.kind === 'called_out');
  const [at, setAt] = useState(el?.atBlock != null ? String(el.atBlock) : ''); const [why, setWhy] = useState(el?.reason ?? '');
  const [inId, setInId] = useState(ei ? String(ei.employeeId) : ''); const [from, setFrom] = useState(ei ? String(ei.fromBlock) : ''); const [to, setTo] = useState(ei ? String(ei.toBlock) : '');
  const people = data.people;
  const leftDone = new Set(d.changes.filter((c) => c.kind === 'left_early' || c.kind === 'called_out').map((c) => c.employeeId));
  const lateDone = new Set(d.changes.filter((c) => c.kind === 'arrived_late').map((c) => c.employeeId));
  const outDone = new Set(d.changes.filter((c) => c.kind === 'called_out').map((c) => c.employeeId));
  const calledDone = new Set(d.changes.filter((c) => c.kind === 'called_in').map((c) => c.employeeId));
  const leftOpts = people.filter((p) => (rows[p.id] ?? OFF) !== OFF).sort((a, b) => a.name.localeCompare(b.name));
  const inOpts = people.filter((p) => !p.away && !d.elsewhere[p.id] && ((rows[p.id] ?? OFF) === OFF || calledDone.has(p.id))).sort((a, b) => a.name.localeCompare(b.name));
  const lrow = leftId ? rows[leftId] ?? OFF : OFF;
  const first = lrow.search(/[^.]/), last = lrow.length - 1 - lrow.split('').reverse().join('').search(/[^.]/);
  const editingLeft = !!leftId && leftDone.has(Number(leftId)), editingIn = !!inId && calledDone.has(Number(inId));
  // leaving early comes after a late arrival; arriving late comes before leaving early
  const leftFrom = leftId ? lateOf(d, Number(leftId))?.b ?? first : first;
  const trow = lateId ? rows[lateId] ?? OFF : OFF;
  const tFirst = trow.search(/[^.]/), tEnd = lateId ? leftOf(d, Number(lateId))?.b ?? trow.length - trow.split('').reverse().join('').search(/[^.]/) : 0;
  const editingLate = !!lateId && lateDone.has(Number(lateId));
  // "Text people to cover…": the rest of a shift someone left (or the whole shift for a call-out), today or later
  const [cov, reloadCov] = useCoverDay(data.location.id, d.date);
  const [pick, setPick] = useState<number | null>(null);
  const openPart = (c: Change) => {
    const r = rows[c.employeeId] ?? OFF, f0 = r.search(/[^.]/), end = r.length - r.split('').reverse().join('').search(/[^.]/);
    const f = c.kind === 'left_early' ? c.atBlock ?? f0 : f0;
    return f0 >= 0 && end - f >= 4 && d.date >= data.today ? { label: `${tlabel(f)}–${tlabel(end)}` } : null;
  };
  const send = async (body: Record<string, unknown>) => {
    try { await post(`/api/schedule/week/${data.week!.id}/day/${day}/changes`, body); await reload(); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t save. Try again.'); }
  };
  const markLeft = () => {
    const id = Number(leftId), p = byId.get(id)!, b = allDay ? first : Number(at);
    const la = lateOf(d, id)?.b ?? null, sc = summarize(actualRow(lrow, null, la)).paidHours, pd = summarize(actualRow(lrow, b, la)).paidHours, w = REASONS[why]!;
    let text = allDay ? `${p.name} will be marked as called out for the whole shift (${w.toLowerCase()}). Their ${sc} scheduled hours will show as 0 paid.`
      : `${p.name} will be marked as leaving at ${tlabel(b)} (${w.toLowerCase()}). Paid hours go from ${sc} to ${pd}.`;
    if (why === 'sick') text += ` ${Math.max(0, sc - pd)} h will be recorded as sick pay and added to the payroll list.`;
    text += ' Their row on the day board will be locked; only a level 9 manager or an admin can edit or undo this.';
    setConfirm({ title: editingLeft ? 'Save this change?' : allDay ? 'Mark as called out?' : 'Mark as left early?', text, ok: editingLeft ? 'Save' : 'OK',
      run: async () => { await send({ kind: allDay ? 'called_out' : 'left_early', employeeId: id, atBlock: allDay ? null : b, reason: why }); setLeftId(''); setAt(''); setWhy(''); setAllDay(false); } });
  };
  const markLate = () => {
    const id = Number(lateId), p = byId.get(id)!, b = Number(lateAt), lb = leftOf(d, id)?.b ?? null;
    const sc = summarize(cutFrom(trow, lb)).paidHours, pd = summarize(actualRow(trow, lb, b)).paidHours;
    setConfirm({ title: editingLate ? 'Save this change?' : 'Mark as arrived late?', ok: editingLate ? 'Save' : 'OK',
      text: `${p.name} will be marked as arriving at ${tlabel(b)} instead of ${tlabel(tFirst)} (${REASONS[lateWhy]!.toLowerCase()}). Paid hours go from ${sc} to ${pd}. Their row on the day board will be locked; only a level 9 manager or an admin can edit or undo this.`,
      run: async () => { await send({ kind: 'arrived_late', employeeId: id, atBlock: b, reason: lateWhy }); setLateId(''); setLateAt(''); setLateWhy(''); } });
  };
  const callIn = () => {
    const id = Number(inId), p = byId.get(id)!, f = Number(from), t = Number(to);
    // their shift, with breaks placed for them only
    const ids = people.map((x) => x.id);
    const all = ids.map((x) => (x === id ? paintRange(OFF, f, t - 1, true) : rows[x] ?? OFF));
    const row = generateBreaks(all, levels, [ids.indexOf(id)], false, seed)[ids.indexOf(id)]!;
    setConfirm({ title: editingIn ? 'Save this change?' : 'Add a called-in shift?', ok: editingIn ? 'Save' : 'OK',
      text: `${p.name} will be ${editingIn ? 'changed to work ' : 'added to the day board, working '}${tlabel(f)}–${tlabel(t)} (${(t - f) / 4} h). Breaks are placed for them automatically.`,
      run: async () => { await send({ kind: 'called_in', employeeId: id, fromBlock: f, toBlock: t, row }); setInId(''); setFrom(''); setTo(''); } });
  };
  const close = () => {
    const left: string[] = [];
    if (leftId) left.push(`${byId.get(Number(leftId))?.name} ${allDay ? 'calling out' : 'leaving early'} (you didn’t press “${editingLeft ? 'Save change' : allDay ? 'Mark as called out' : 'Mark as left'}”)`);
    if (lateId) left.push(`${byId.get(Number(lateId))?.name} arriving late (you didn’t press “${editingLate ? 'Save change' : 'Mark as late'}”)`);
    if (inId) left.push(`${byId.get(Number(inId))?.name} being called in (you didn’t press “${editingIn ? 'Save change' : 'Add shift'}”)`);
    if (!left.length) { onClose(); return; }
    setConfirm({ title: 'This change hasn’t been applied', text: `You started ${left.join(' and ')}. If you close now, it won’t be saved. Press Cancel to go back and apply it.`, ok: 'Close without saving', run: onClose });
  };
  return (
    <Modal title={`Mid-day changes · ${DAY_FULL[day]} ${md(d.date)}`} onClose={close} wide>
      <div style={{ fontSize: 13, color: '#4A5561' }}>Use these when the day changes after the schedule is made. <b>Left early</b>: the rest of their shift stays on the day board, greyed out, and their paid hours drop; scheduled hours still show. <b>Arrived late</b>: the same for the start of their shift, up to when they arrived. <b>Called in</b>: adds a shift for someone who was off, with breaks placed for them only. Employees see these changes right away.</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 28px' }}>
        <div className="midrow"><span className="midlbl" title="Left during the shift, or called out for the whole shift (tick All day)">Left early</span>
          <select className="sel-sm" value={leftId} onChange={(e) => { setLeftId(e.target.value); setAt(''); const c = d.changes.find((x) => x.employeeId === Number(e.target.value) && x.kind !== 'called_in'); if (c) { setAllDay(c.kind === 'called_out'); setAt(c.atBlock != null ? String(c.atBlock) : ''); setWhy(c.reason ?? ''); } }} aria-label="Employee who left early">
            <option value="">Who left?</option>{leftOpts.map((p) => <option key={p.id} value={p.id}>{p.name}{leftDone.has(p.id) ? ' (edit)' : ''}</option>)}
          </select>
          <label className="allday"><input type="checkbox" checked={allDay} disabled={!!leftId && lateDone.has(Number(leftId))} title={leftId && lateDone.has(Number(leftId)) ? 'They’re marked as arriving late, so they didn’t call out.' : ''} onChange={(e) => { setAllDay(e.target.checked); setAt(''); }} />All day</label>
          <span>at</span>
          <select className="sel-sm" value={at} onChange={(e) => setAt(e.target.value)} disabled={allDay || !leftId} aria-label="Time they left">
            <option value="">Time</option>{leftId && first >= 0 && Array.from({ length: Math.max(0, last - leftFrom) }, (_, i) => leftFrom + 1 + i).map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}
          </select>
          <select className="sel-sm" value={why} onChange={(e) => setWhy(e.target.value)} aria-label="Reason for leaving early">
            <option value="">Reason</option>{Object.entries(REASONS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button className="tbtn" disabled={!leftId || (!allDay && at === '') || !why || (editingLeft && !data.canFix)} onClick={markLeft}>{editingLeft ? 'Save change' : allDay ? 'Mark as called out' : 'Mark as left'}</button>
        </div>
        <div className="midrow"><span className="midlbl" title="Came in after the start of their shift">Arrived late</span>
          <select className="sel-sm" value={lateId} onChange={(e) => { setLateId(e.target.value); const c = lateOf(d, Number(e.target.value))?.c; setLateAt(c?.atBlock != null ? String(c.atBlock) : ''); setLateWhy(c?.reason ?? ''); }} aria-label="Employee who arrived late">
            <option value="">Who?</option>{leftOpts.filter((p) => !outDone.has(p.id)).map((p) => <option key={p.id} value={p.id}>{p.name}{lateDone.has(p.id) ? ' (edit)' : ''}</option>)}
          </select>
          <span>at</span>
          <select className="sel-sm" value={lateAt} onChange={(e) => setLateAt(e.target.value)} disabled={!lateId} aria-label="Time they arrived">
            <option value="">Time</option>{lateId && tFirst >= 0 && Array.from({ length: Math.max(0, tEnd - tFirst - 1) }, (_, i) => tFirst + 1 + i).map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}
          </select>
          <select className="sel-sm" value={lateWhy} onChange={(e) => setLateWhy(e.target.value)} aria-label="Reason for arriving late">
            <option value="">Reason</option>{Object.entries(LATE_REASONS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button className="tbtn" disabled={!lateId || lateAt === '' || !lateWhy || (editingLate && !data.canFix)} onClick={markLate}>{editingLate ? 'Save change' : 'Mark as late'}</button>
        </div>
        <div className="midrow"><span className="midlbl">Called in</span>
          <select className="sel-sm" value={inId} onChange={(e) => { setInId(e.target.value); const c = d.changes.find((x) => x.employeeId === Number(e.target.value) && x.kind === 'called_in'); setFrom(c ? String(c.fromBlock) : ''); setTo(c ? String(c.toBlock) : ''); }} aria-label="Employee called in">
            <option value="">Who came in?</option>{inOpts.map((p) => <option key={p.id} value={p.id}>{p.name}{calledDone.has(p.id) ? ' (edit)' : ''}</option>)}
          </select>
          <span>from</span>
          <select className="sel-sm" value={from} onChange={(e) => { setFrom(e.target.value); setTo(''); }} aria-label="Start time">
            <option value="">Start</option>{Array.from({ length: d.dayEnd - d.dayStart }, (_, i) => d.dayStart + i).map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}
          </select>
          <span>until</span>
          <select className="sel-sm" value={to} onChange={(e) => setTo(e.target.value)} aria-label="End time">
            <option value="">End</option>{from !== '' && Array.from({ length: Math.max(0, d.dayEnd + 1 - (Number(from) + 4)) }, (_, i) => Number(from) + 4 + i).map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}
          </select>
          <button className="tbtn" disabled={!inId || from === '' || to === '' || (editingIn && !data.canFix)} onClick={callIn}>{editingIn ? 'Save change' : 'Add shift'}</button>
        </div>
      </div>
      {d.changes.map((c) => (
        <div key={c.id} className="chgwrap"><div className="chg"><span>{changeText(c, d, byId)}</span></div>
          {!counter && (c.kind === 'left_early' || c.kind === 'called_out') && <CoverLine open={openPart(c)} cover={cov?.covers.find((x) => x.changeId === c.id)} canGive={!!cov?.canGive && data.canMidDay}
            onPick={() => setPick(c.id)} setConfirm={setConfirm} onGiven={async () => { await reload(); await reloadCov(); }} showToast={showToast} />}
        </div>
      ))}
      {pick != null && <CoverPick changeId={pick} onClose={() => setPick(null)} onSent={reloadCov} showToast={showToast} />}
      {!data.canFix && <div style={{ fontSize: 12, color: '#7A4A06' }}>Only a level 9 manager or an admin can edit or undo a change.</div>}
      <div style={{ fontSize: 12, color: '#4A5561' }}>The day board updates as soon as you press OK. Someone who left or called out shows greyed out on the day board, with paid and scheduled hours.</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="tbtn primary" style={{ minWidth: 90 }} onClick={close}>Done</button></div>
    </Modal>
  );
}

/**
 * "Mid-day change…" on the counter screen (managers 7–9 who unlocked it): the same form as the day board,
 * for today at that screen's store.
 */
export function CounterMidDay({ locationId, date, onClose, onDone }: { locationId: number; date: string; onClose: () => void; onDone: () => void }) {
  const [data, setData] = useState<WeekResp | null>(null);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [toast, showToast] = useToast();
  const start = isoAdd(date, -new Date(date + 'T12:00:00Z').getUTCDay());
  const load = useCallback(async () => {
    try { setData(await get<WeekResp>(`/api/schedule/week?location=${locationId}&start=${start}`)); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'Can’t load today’s schedule.'); }
  }, [locationId, start, showToast]);
  useEffect(() => { void load(); }, [load]);
  const day = data ? data.days.findIndex((x) => x.date === date) : -1;
  const d = data?.days[day];
  return (
    <>
      {data && d && (
        <MidDay data={data} d={d} day={day} rows={d.rows}
          levels={data.people.map((p) => breakLevel(p.level, p.canManagerSpot, p.canLeadSpot))} seed={d.breakSeed}
          byId={new Map(data.people.map((p) => [p.id, p]))} setConfirm={setConfirm}
          onClose={onClose} reload={async () => { await load(); onDone(); }} showToast={showToast} counter />
      )}
      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
      {toast}
    </>
  );
}

/**
 * "Schedule at another store…" (prototype): one of this store's people works at another store this day; their
 * hours count here and they show in light blue on both boards. "Add from Operations…" (admins): a tech or office
 * person fills in at this store.
 */
function LendModal({ mode, data, d, day, weekHours, onClose, onDone, setConfirm, showToast }: {
  mode: 'lend' | 'office'; data: WeekResp; d: Day; day: number; weekHours: Map<number, { hrs: number; days: number }>;
  onClose: () => void; onDone: () => Promise<void>; setConfirm: (c: ConfirmSpec) => void; showToast: (m: string) => void;
}) {
  const [who, setWho] = useState('');
  const [to, setTo] = useState(mode === 'office' ? String(data.location.id) : '');
  const [from, setFrom] = useState('');
  const [until, setUntil] = useState('');
  const [ops, setOps] = useState<{ id: number; name: string; position: string | null; level: number | null }[] | null>(null);
  useEffect(() => { if (mode === 'office') get<{ people: typeof ops }>('/api/share/office-people').then((r) => setOps(r.people ?? [])).catch(() => setOps([])); }, [mode]);
  const mine = data.people.filter((p) => !p.away && !p.office).sort((a, b) => a.name.localeCompare(b.name));
  const opts = mode === 'office'
    ? (ops ?? []).map((p) => ({ id: p.id, label: `${p.name}${p.position ? ` · ${p.position}` : ''}${p.level != null ? ` · store level ${p.level}` : ''}${d.rows[p.id] ? ' (already here today)' : ''}` }))
    : mine.map((p) => ({ id: p.id, label: `${p.name} · ${weekHours.get(p.id)?.hrs ?? 0}h this week${d.sharedOut[p.id] ? ` (at ${d.sharedOut[p.id]!.store} today)` : d.rows[p.id] ? ' (on today’s schedule)' : ''}` }));
  const person = mode === 'office' ? ops?.find((p) => String(p.id) === who) : mine.find((p) => String(p.id) === who);
  const storeName = mode === 'office' ? data.location.name : data.otherStores.find((x) => String(x.id) === to)?.name ?? '';
  const f = from === '' ? null : Number(from), t = until === '' ? null : Number(until);
  const p2 = mode === 'lend' ? mine.find((p) => String(p.id) === who) : null;
  const toff = p2?.timeOff[d.date];
  // the day's hours: this store's own day for Operations fill-ins (it may be an overnight party); a normal day elsewhere
  const lo = mode === 'office' ? d.dayStart : 0, hi = mode === 'office' ? d.dayEnd : DAY_BLOCKS;
  const blocks = Array.from({ length: hi - lo }, (_, i) => lo + i);
  const go = () => {
    if (!person || f == null || t == null) return;
    const has = mode === 'lend' && (d.rows[person.id] || d.sharedOut[person.id]);
    const name = person.name, when = `${DAY_FULL[day]} ${md(d.date)}, ${tlabel(f)}–${tlabel(t)}`;
    setConfirm(mode === 'office' ? {
      title: `Add ${name} to ${storeName}?`,
      text: `${name} (Operations) will work at ${storeName} on ${when}, with breaks placed for them. ${storeName}’s managers will see them on this day board and can move their breaks, but only an admin can change their hours or take them off the day. They see the shift once ${storeName} posts the week (or posts changes).`,
      ok: 'Add to this day', run: async () => { await send(); },
    } : {
      title: `Schedule ${name} at ${storeName}?`,
      text: `${name} will work at ${storeName} on ${when}. Their hours count at ${data.location.name}. ${has ? 'This replaces their shift today. ' : ''}They’ll show in light blue on both boards, and ${storeName}’s manager handles their breaks and rotation. ${name} sees it once you post (Post week or Post changes).`,
      ok: 'Schedule', run: async () => { await send(); },
    });
  };
  const send = async () => {
    try { await post('/api/share', { employeeId: Number(who), date: d.date, toLocationId: Number(to), fromBlock: f, toBlock: t }); await onDone(); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t work. Try again.'); }
  };
  const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' };
  return (
    <Modal title={mode === 'office' ? `Add someone from Operations · ${DAY_FULL[day]} ${md(d.date)}` : `Schedule someone at another store · ${DAY_FULL[day]} ${md(d.date)}`} onClose={onClose}>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: '#4A5561' }}>
        {mode === 'office'
          ? `Pick a tech or office person to fill in at ${data.location.name} this day. Their store level decides which stations they can work in the rotation.`
          : `Pick one of your people and the store they’ll work at. Their hours still count at ${data.location.name}. They show in light blue on that store’s board, where its manager handles their breaks and rotation, and in light blue here too.`}
      </div>
      <label style={lbl}>{mode === 'office' ? 'Who (Operations)' : 'Who'}
        <select className="sel-sm" value={who} onChange={(e) => setWho(e.target.value)} aria-label="Who">
          <option value="">{mode === 'office' && !ops ? 'Loading…' : 'Who?'}</option>
          {opts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select></label>
      {mode === 'office' && ops && ops.length === 0 && <div className="fieldnote">No one is on the Operations list yet. Add them on the Employees page with Operations picked as the location.</div>}
      {mode === 'lend' && (
        <label style={lbl}>Store
          <select className="sel-sm" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Store">
            <option value="">Choose a store</option>
            {data.otherStores.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select></label>
      )}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label style={lbl}>Start
          <select className="sel-sm" value={from} onChange={(e) => { setFrom(e.target.value); setUntil(''); }} aria-label="Start time">
            <option value="">Start</option>{blocks.map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}
          </select></label>
        <label style={lbl}>End
          <select className="sel-sm" value={until} onChange={(e) => setUntil(e.target.value)} aria-label="End time" disabled={f == null}>
            <option value="">End</option>{f != null && Array.from({ length: Math.max(0, hi - f - 3) }, (_, i) => f + 4 + i).map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}
          </select></label>
      </div>
      {toff && <div className="fieldnote bad">{p2!.name} has approved time off {toff.allDay ? 'all day' : `${tlabel(toff.fromBlock ?? 0)}–${tlabel(toff.toBlock ?? 0)}`} that day.</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>Cancel</button>
        <button className="tbtn primary" disabled={!who || !to || f == null || t == null} onClick={go} style={{ minWidth: 110 }}>{mode === 'office' ? 'Add…' : 'Schedule…'}</button>
      </div>
    </Modal>
  );
}

/**
 * Changes not posted yet (owner 11/2: "i need a way to review and maybe clear this message … if changes were made that were
 * unintentional i need to be able to put it back without having to notify anyone"). Each person and day: the posted shift next to
 * the shift now, what's different, and who changed it last. "Put back as posted" restores the ticked ones (nobody is told:
 * employees already see the posted copy). "Post changes" shows employees the new shifts (no texts).
 */
interface ReviewItem {
  employeeId: number; name: string; date: string; day: number; what: string; canPutBack: boolean; why: string | null; by: string | null;
  posted: { shift: string; breaks: string[]; store: string | null }; now: { shift: string; breaks: string[]; store: string | null };
}
function ReviewChanges({ weekId, canEdit, onClose, onChanged, showToast }: { weekId: number; canEdit: boolean; onClose: () => void; onChanged: () => Promise<void> | void; showToast: (t: string) => void }) {
  const [items, setItems] = useState<ReviewItem[] | null>(null);
  const [pick, setPick] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const key = (x: { employeeId: number; date: string }) => `${x.employeeId}|${x.date}`;
  useEffect(() => {
    get<{ items: ReviewItem[] }>(`/api/schedule/week/${weekId}/unposted`).then((r) => setItems(r.items)).catch((e) => setErr(e instanceof ApiError ? e.message : 'Couldn’t load the changes.'));
  }, [weekId]);
  const can = (items ?? []).filter((x) => x.canPutBack);
  const putBack = async () => {
    const chosen = can.filter((x) => pick.has(key(x)));
    if (!chosen.length) return;
    setBusy(true); setErr('');
    try {
      const r = await post<{ done: string[]; skipped: string[]; items: ReviewItem[] }>(`/api/schedule/week/${weekId}/put-back`, { items: chosen.map((x) => ({ employeeId: x.employeeId, date: x.date })) });
      setItems(r.items); setPick(new Set());
      if (r.skipped.length) setErr(`Not put back: ${r.skipped.join(' · ')}`);
      showToast(`Put back as posted: ${r.done.length} ${r.done.length === 1 ? 'shift' : 'shifts'}. Nobody was notified.`);
      await onChanged();
      if (!r.items.length) onClose();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t work. Try again.'); }
    finally { setBusy(false); }
  };
  const postAll = async () => {
    setBusy(true); setErr('');
    try { await post(`/api/schedule/week/${weekId}/post-changes`); showToast('Changes posted. Employees see them in the app now (no texts were sent).'); await onChanged(); onClose(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t work. Try again.'); setBusy(false); }
  };
  const shiftWords = (x: ReviewItem['posted']) => x.shift === 'Off' ? <i>Off</i> : <>{x.shift}{x.store ? ` · ${x.store}` : ''}{x.breaks.length ? <div className="rvbrk">{x.breaks.join(' · ')}</div> : null}</>;
  const all = can.length > 0 && can.every((x) => pick.has(key(x)));
  return (
    <Modal title="Changes not posted yet" onClose={onClose} wide>
      <p style={{ margin: '4px 0 10px', fontSize: 14 }}>Employees still see the <b>posted</b> schedule. Nothing below has been sent to anyone.
        Tick anything that changed by mistake and press <b>Put back as posted</b> (nobody is told), or press <b>Post changes</b> to show employees the new shifts.</p>
      {!items && !err && <div>Loading…</div>}
      {items && items.length === 0 && <div style={{ padding: 10, background: '#E3F6EA', borderRadius: 6 }}>Everything matches the posted schedule. There’s nothing to post.</div>}
      {items && items.length > 0 && (
        <div className="rvwrap">
          <table className="rvtable">
            <thead><tr>
              <th>{canEdit && can.length > 0 && <input type="checkbox" aria-label="Tick all" checked={all} onChange={(e) => setPick(new Set(e.target.checked ? can.map(key) : []))} />}</th>
              <th>Who · day</th><th>Posted (what employees see)</th><th>Now</th><th>What’s different</th><th>Last changed by</th>
            </tr></thead>
            <tbody>
              {items.map((x) => (
                <tr key={key(x)}>
                  <td>{canEdit && x.canPutBack && <input type="checkbox" aria-label={`Put back ${x.name} ${x.date}`} checked={pick.has(key(x))}
                    onChange={(e) => setPick((p) => { const n = new Set(p); if (e.target.checked) n.add(key(x)); else n.delete(key(x)); return n; })} />}</td>
                  <td><b>{x.name}</b><div className="rvbrk">{DAY_FULL[x.day]} {Number(x.date.slice(5, 7))}/{Number(x.date.slice(8, 10))}</div></td>
                  <td>{shiftWords(x.posted)}</td>
                  <td>{shiftWords(x.now)}</td>
                  <td>{x.what}{x.why && <div className="rvbrk" style={{ color: '#7A4A06' }}>{x.why}</div>}</td>
                  <td className="rvbrk">{x.by ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {err && <div className="errbar" style={{ marginTop: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
        <button className="btn2" onClick={onClose}>Close</button>
        {canEdit && items && items.length > 0 && <>
          <button className="btn2" disabled={busy || !can.some((x) => pick.has(key(x)))} onClick={putBack}>Put back as posted{pick.size ? ` (${can.filter((x) => pick.has(key(x))).length})` : ''}</button>
          <button className="tbtn primary" disabled={busy} onClick={postAll} title="Employees see the new shifts in the app, marked “Updated”. No texts are sent.">Post changes</button>
        </>}
      </div>
    </Modal>
  );
}
